import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AppShell } from './layout/AppShell';
import { RequireAuth } from './auth/RequireAuth';
import { LoadingBlock } from './components/ui/Misc';

const LoginPage = lazy(() => import('./features/login/LoginPage'));
const LandingPage = lazy(() => import('./features/landing/LandingPage'));
const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'));
const ComprobantesPage = lazy(() => import('./features/comprobantes/ComprobantesPage'));
const ComprobanteDetailPage = lazy(() => import('./features/comprobantes/ComprobanteDetailPage'));
const NuevoComprobantePage = lazy(() => import('./features/emitir/NuevoComprobantePage'));
const ClientesPage = lazy(() => import('./features/clientes/ClientesPage'));
const ProductosPage = lazy(() => import('./features/productos/ProductosPage'));
const CuentaPage = lazy(() => import('./features/cuenta/CuentaPage'));
const OnboardingPage = lazy(() => import('./features/onboarding/OnboardingPage'));
const TerminosPage = lazy(() => import('./features/legal/TerminosPage'));
const PrivacidadPage = lazy(() => import('./features/legal/PrivacidadPage'));
const EliminarCuentaPage = lazy(() => import('./features/legal/EliminarCuentaPage'));
const SoportePage = lazy(() => import('./features/legal/SoportePage'));
const PlanesPage = lazy(() => import('./features/planes/PlanesPage'));
const CambiarPlanPage = lazy(() => import('./features/planes/CambiarPlanPage'));
const ApiPage = lazy(() => import('./features/api/ApiPage'));
const ClavesTab = lazy(() => import('./features/api/ClavesTab'));
const WebhooksTab = lazy(() => import('./features/api/WebhooksTab'));
const UsoTab = lazy(() => import('./features/api/UsoTab'));
const DocsTab = lazy(() => import('./features/api/DocsTab'));

export function App() {
  return (
    <Suspense fallback={<LoadingBlock />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/landing" element={<LandingPage />} />
        {/* Public: linkable from the footer, the app stores and the SRI paperwork. */}
        <Route path="/terminos" element={<TerminosPage />} />
        <Route path="/privacidad" element={<PrivacidadPage />} />
        {/* Google Play requires this one to be reachable without signing in. */}
        <Route path="/eliminar-cuenta" element={<EliminarCuentaPage />} />
        {/* The App Store support URL: a reviewer opens it signed out and expects a way to reach us. */}
        <Route path="/soporte" element={<SoportePage />} />
        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              <OnboardingPage />
            </RequireAuth>
          }
        />
        {/* Outside the app shell on purpose: a charge gets the whole window, with nothing to
            click but confirm and back — the same reason Stripe Checkout is its own page. */}
        <Route
          path="/suscripcion/cambiar"
          element={
            <RequireAuth>
              <CambiarPlanPage />
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="comprobantes" element={<ComprobantesPage />} />
          <Route path="comprobantes/nuevo" element={<NuevoComprobantePage />} />
          <Route path="comprobantes/:id" element={<ComprobanteDetailPage />} />
          <Route path="clientes" element={<ClientesPage />} />
          <Route path="productos" element={<ProductosPage />} />
          <Route path="cuenta" element={<CuentaPage />} />
          <Route path="planes" element={<PlanesPage />} />
          {/* One shell, four tabs: the ambiente switch and the plan gate belong to all of them.
              NOT mounted at /api: the dev proxy and CloudFront both route that prefix to the
              backend, so a reload on /api/docs is answered by the API instead of the SPA. */}
          <Route path="integraciones" element={<ApiPage />}>
            <Route index element={<ClavesTab />} />
            <Route path="webhooks" element={<WebhooksTab />} />
            <Route path="uso" element={<UsoTab />} />
            <Route path="docs" element={<DocsTab />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
